import * as React from 'react';
import { Col, Table, Select, Tag, Row, Tooltip, Switch, Checkbox } from 'antd';
import { NotificationTypes, showNotification } from '../../components/Notifications';
import {
  StaffUserType,
  districtJobFunctions,
  schoolJobFunctions,
  JobFunctions,
  AvailableCodes,
} from '../../utils/constants';
import apiClient from '../../utils/apiClient';
import { IntegrationContext } from '../../utils/context';
import { CloseOutlined, ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { changeTitlesMapping, changeJobFunctionMapping, availableOneRosterSisCodes, getColumnWidth } from '../../utils/oneRosterStaffUtils';
const { Option } = Select; 

interface OneRosterRoleAndJobFunctionProps {
  roleMapping: Record<string, any>;
  setRoleMapping: (setRoleMapping: Record<string, any>) => void;
  staffUserType: StaffUserType;
  setNavRolesMap: (setNavRolesMap: any) => void;
  switchChecked?: boolean;
  setSwitchChecked?: (setSwitchChecked: boolean) => void;
  sisStaffRoles: {
    label: string;
    value: string;
  }[]
}

const tagColor = '#f5f5f5';
const tagTextColor = '#141497';

const OneRosterRoleAndJobFunction = ({
  roleMapping,
  setRoleMapping,
  staffUserType,
  setNavRolesMap,
  switchChecked,
  setSwitchChecked,
  sisStaffRoles,
}: OneRosterRoleAndJobFunctionProps): React.ReactElement => {
  const isMountedRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [navRoles, setNavRoles] = React.useState([]);
  const [staffUsedRoles, setStaffUsedRoles] = React.useState([]);
  const [validationErrorRows, setValidationErrorRows] = React.useState({});
  const { districtId } = React.useContext(IntegrationContext);
  const [navDefaultRoles, setNavDefaultRoles] = React.useState([]);

  const availableNavRoles = (): AvailableCodes[] =>
    availableOneRosterSisCodes<AvailableCodes>({}, navDefaultRoles);

  const availableSisRoles = (): AvailableCodes[] => availableOneRosterSisCodes<AvailableCodes>(roleMapping, sisStaffRoles);

  let jobFunctions;

  if (staffUserType === StaffUserType.school) {
    jobFunctions = schoolJobFunctions;
  } else {
    jobFunctions = districtJobFunctions;
  }

  const getNavianceRolesMap = (data): any => {
    const navRolesMap = {};
    data.forEach((role) => {
      if (navRolesMap[role.name]) {
        navRolesMap[role.name] = [
          ...navRolesMap[role.name],
          {
            institutionId: role.institutionId,
            id: role.id,
          },
        ];
      } else {
        navRolesMap[role.name] = [
          {
            institutionId: role.institutionId,
            id: role.id,
          },
        ];
      }
    });
    return navRolesMap;
  };

  React.useEffect(() => {
    setNavRoles(availableNavRoles());
  }, [navDefaultRoles]);

  React.useEffect(() => {
    setStaffUsedRoles(availableSisRoles());
  }, [roleMapping, sisStaffRoles]);

  const getNavianceRolesList = async () => {
    try {
      setLoading(true);
      let { data } = await apiClient.get(`/roles/userroles?userType=${staffUserType}`);
      // Fix for not showing roles for inactive school or schools with no account
      if (staffUserType === StaffUserType.school) {
        const navSchools = await apiClient.get('/highschools/v2?byType=district&includeInactive=false');
        const activeNavSchools = navSchools.data.map((navSchool) => navSchool.nid);
        activeNavSchools.push(districtId);
        data = data.filter((record) => activeNavSchools.includes(record.institutionId));
      }

      const navRolesMap = getNavianceRolesMap(data);

      // To remove role Mapping which doesn't exist anymore
      for (const key in roleMapping) {
        if (!navRolesMap[key]) {
          delete roleMapping[key];
        }
      }
      setNavRolesMap(navRolesMap);
      const navianceRoles = Object.keys(navRolesMap).map((key) => {
        return {
          label: key,
          value: key,
          key: key,
        };
      });
      setNavRoles(navianceRoles);
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      showNotification(
        NotificationTypes.error,
        `Error Getting ${staffUserType} Level Roles`,
        'Failure in getting data from server.',
      );
    }
  };

  React.useEffect(() => {
    isMountedRef.current = true;
    getNavianceRolesList();
    () => (isMountedRef.current = false);
  }, []);

  const roleTableColumns = [
    {
      title: 'Naviance Field: Role',
      width: '30%',
      render: (data) => (
        <div style={{ marginTop: '1em' }}>
          {data.label}
        </div>
      ),
      key: 'naviance_role',
    },
    {
      title: `OneRoster Role(s): Role in file`,
      width: getColumnWidth(staffUserType).roleWidth,
      render: (data) => (
        <Select
          size="large"
          className="select select-responsive"
          allowClear
          mode="multiple"
          {...(validationErrorRows[data.value]
            ? { status: 'error', suffixIcon: <ExclamationCircleFilled style={{ color: '#FF0000' }} /> }
            : {})}
          data-test-id={`${staffUserType}_role_select_${data.value}`}
          value={roleMapping[data.value]?.titles}
          onChange={(val) =>
            changeTitlesMapping(
              data.value,
              val,
              roleMapping,
              setRoleMapping,
              setValidationErrorRows,
              setLoading,
              staffUserType,
            )
          }
        >
          {staffUsedRoles.map((role, ind) => (
            <Option
              className="selectOptions"
              key={`${staffUserType}-role-${data.value}-${role.value}-${ind}`}
              value={role.value}
              data-test-id={`${staffUserType}_role_option_${role.label}`}
              disabled={role.used && !roleMapping[data.value]?.titles?.includes(role.value)}
            >
              {role.label}
            </Option>
          ))}
        </Select>
      ),
      key: 'sis_role',
    },
    {
      title: (
        <span>
          Job function
          <Tooltip title="As assigned in Naviance user accounts. Selecting Counselor or Teacher adds the user to the respective list in Naviance.">
            <InfoCircleFilled style={{ marginLeft: '8px', cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      width: getColumnWidth(staffUserType).jobFunctionWidth,
      render: (data) => (
        <>
          <Select
            className="select select-responsive"
            data-test-id={`${staffUserType}_jobFunction_select_${data.value}`}
            placeholder="Select"
            value={
              roleMapping[data.value]?.jobFunction || roleMapping[data.value]?.jobFunction === 0
                ? roleMapping[data.value]?.jobFunction
                : undefined
            }
            disabled={!(roleMapping[data.value]?.titles && roleMapping[data.value]?.titles?.length)}
            {...(roleMapping[data.value]?.titles &&
            roleMapping[data.value]?.titles?.length &&
            roleMapping[data.value]?.jobFunction !== 0 &&
            !roleMapping[data.value]?.jobFunction
              ? { status: 'error', suffixIcon: <ExclamationCircleFilled style={{ color: '#FF0000' }} /> }
              : {})}
            onChange={(val) =>
              changeJobFunctionMapping(
                data.value,
                val,
                roleMapping,
                staffUserType,
                setRoleMapping,
                setLoading,
                setValidationErrorRows,
              )
            }
            options={jobFunctions.map((jobFunction) => ({
              label: jobFunction.description,
              value: jobFunction.code,
            }))}
          />
          {roleMapping[data.value]?.titles?.length &&
          roleMapping[data.value]?.jobFunction !== 0 &&
          !roleMapping[data.value]?.jobFunction ? (
            <div>
              <p style={{ color: '#FF0000', marginTop: '0.4em' }}>Required field</p>
            </div>
          ) : (
            <></>
          )}
        </>
      ),
      key: 'job_function',
    },
  ];

  if (staffUserType === StaffUserType.school) {
    roleTableColumns.push(
        {
          title: (
            <span>
              Permissions
              <Tooltip title="Staff in the Teacher List will available for students to select when requesting Letters of Recommendation. Staff in the Counselor List can be assigned as a student's counselor and will also be available as a filter option in certain reports.">
                <InfoCircleFilled style={{ marginLeft: '8px', cursor: 'pointer' }} />
              </Tooltip>
            </span>
          ),
          width: '15%',
          render: (data) => (
            <div style={{ marginTop: '1em' }}>
              <Checkbox
                checked={
                  roleMapping[data.value]?.isTeacher ??
                  roleMapping[data.value]?.jobFunction === JobFunctions.SCHOOL_TEACHER
                }
                data-test-id="TeacherList"
                disabled={!(roleMapping[data.value]?.titles && roleMapping[data.value]?.titles?.length)}
                onChange={(e) => {
                  setRoleMapping({
                    ...roleMapping,
                    [data.value]: {
                      ...roleMapping[data.value],
                      isTeacher: Number(e.target.checked),
                    },
                  });
                }}
              >
                Teacher
              </Checkbox>
              <Checkbox
                checked={
                  roleMapping[data.value]?.isCounselor ??
                  roleMapping[data.value]?.jobFunction === JobFunctions.SCHOOL_COUNSELOR
                }
                data-test-id="CounselorList"
                disabled={!(roleMapping[data.value]?.titles && roleMapping[data.value]?.titles?.length)}
                onChange={(e) => {
                  setRoleMapping({
                    ...roleMapping,
                    [data.value]: {
                      ...roleMapping[data.value],
                      isCounselor: Number(e.target.checked),
                    },
                  });
                }}
              >
                Counselor
              </Checkbox>
            </div>
          ),
          key: 'permissions',
        }
    );
  }

  return (
    <Row>
      <Col span={24}>
        <Table
          className="oneRosterStaffDefinedCodes"
          columns={roleTableColumns}
          dataSource={navRoles}
          pagination={false}
          loading={loading}
          rowKey="key"
        />
      </Col>
    </Row>
  );
};

export default OneRosterRoleAndJobFunction;
